// A fully customizable, animated soroban (Japanese abacus).
// Each rod has one "heaven" bead (value 5) above the reckoning bar and four
// "earth" beads (value 1 each) below it. Beads slide toward the bar when active.
import React, { useMemo } from "react";
import { rodDigit, rodsToNumber, formatNumber } from "./mathUtils";

const BEAD_UNIT = 26; // base bead height in px (scaled by settings.size)

function beadClip(shape) {
  if (shape === "round") return "ellipse(50% 50% at 50% 50%)";
  if (shape === "hex") return "polygon(25% 0, 75% 0, 100% 50%, 75% 100%, 25% 100%, 0 50%)";
  // diamond (classic soroban lens)
  return "polygon(50% 4%, 92% 50%, 50% 96%, 8% 50%)";
}

export default function AbacusBoard({ rods, settings, onChange, interactive = true }) {
  const s = settings;
  const scale = s.size || 1;
  const bead = Math.round(BEAD_UNIT * scale);
  const rodWidth = Math.max(30, Math.round(bead * 1.55));
  const heavenTrack = bead * 2;   // one bead + one empty slot
  const earthTrack = bead * 5;    // four beads + one empty slot

  const total = useMemo(() => rodsToNumber(rods), [rods]);
  const placeCount = rods.length;

  function setRod(index, next) {
    if (!interactive || !onChange) return;
    const copy = rods.map((r, i) => (i === index ? next : r));
    onChange(copy);
  }

  function toggleHeaven(index) {
    const r = rods[index];
    setRod(index, { ...r, heaven: !r.heaven });
  }

  // Earth beads are numbered 1..4 from the bar downward. Clicking bead j either
  // pushes beads up to the bar or releases them, matching real soroban motion.
  function clickEarth(index, j) {
    const r = rods[index];
    const next = j <= r.earth ? j - 1 : j;
    setRod(index, { ...r, earth: next });
  }

  return (
    <div
      className={`abx-board ${interactive ? "" : "abx-board--display"}`}
      style={{ "--abx-frame": s.frameColor, "--abx-rod": s.rodColor, "--abx-bar": s.barColor }}
    >
      <div className="abx-frame">
        <div className="abx-rods">
          {rods.map((rod, index) => {
            const place = placeCount - index - 1;
            return (
              <div className="abx-rod" key={index} style={{ width: rodWidth }}>
                {/* Heaven section */}
                <div className="abx-heaven" style={{ height: heavenTrack }}>
                  <span className="abx-wire" />
                  <button
                    type="button"
                    className={`abx-bead abx-bead--heaven ${rod.heaven ? "is-active" : ""}`}
                    style={{
                      width: rodWidth - 4,
                      height: bead,
                      transform: `translateY(${rod.heaven ? bead : 0}px)`,
                      background: `linear-gradient(145deg, ${lighten(s.beadColorHeaven)}, ${s.beadColorHeaven})`,
                      clipPath: beadClip(s.beadShape),
                      cursor: interactive ? "pointer" : "default",
                    }}
                    onClick={() => toggleHeaven(index)}
                    aria-label={`Heaven bead, rod ${place}`}
                    tabIndex={interactive ? 0 : -1}
                  />
                </div>

                <span className="abx-bar" />

                {/* Earth section */}
                <div className="abx-earth" style={{ height: earthTrack }}>
                  <span className="abx-wire" />
                  {[1, 2, 3, 4].map((j) => {
                    const active = j <= rod.earth;
                    const slot = (j - 1) + (active ? 0 : 1);
                    return (
                      <button
                        type="button"
                        key={j}
                        className={`abx-bead abx-bead--earth ${active ? "is-active" : ""}`}
                        style={{
                          width: rodWidth - 4,
                          height: bead,
                          transform: `translateY(${slot * bead}px)`,
                          background: `linear-gradient(145deg, ${lighten(s.beadColorEarth)}, ${s.beadColorEarth})`,
                          clipPath: beadClip(s.beadShape),
                          cursor: interactive ? "pointer" : "default",
                        }}
                        onClick={() => clickEarth(index, j)}
                        aria-label={`Earth bead ${j}, rod ${place}`}
                        tabIndex={interactive ? 0 : -1}
                      />
                    );
                  })}
                </div>

                {s.showValues && (
                  <div className="abx-place" style={{ fontSize: Math.max(9, 11 * scale) }}>
                    {place === 0 ? "1" : `10${superscript(place)}`}
                  </div>
                )}
                {s.showDigits && (
                  <div className="abx-digit" style={{ fontSize: Math.max(13, 16 * scale) }}>
                    {rodDigit(rod)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {s.showTotal && (
        <div className="abx-total">
          <span className="abx-total-eq">Σ =</span>
          <span className="abx-total-value">{formatNumber(total)}</span>
        </div>
      )}
    </div>
  );
}

function superscript(n) {
  const map = { 0: "⁰", 1: "¹", 2: "²", 3: "³", 4: "⁴", 5: "⁵", 6: "⁶", 7: "⁷", 8: "⁸", 9: "⁹" };
  return String(n).split("").map((d) => map[d] || "").join("");
}

// Lightens a hex color for the bead highlight gradient.
function lighten(hex) {
  const c = normalizeHex(hex);
  if (!c) return hex;
  const amt = 60;
  const r = Math.min(255, ((c >> 16) & 0xff) + amt);
  const g = Math.min(255, ((c >> 8) & 0xff) + amt);
  const b = Math.min(255, (c & 0xff) + amt);
  return `rgb(${r}, ${g}, ${b})`;
}

function normalizeHex(hex) {
  if (typeof hex !== "string") return null;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((x) => x + x).join("");
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  return Number.isNaN(n) ? null : n;
}
